/**
 * Environment-aware logger.
 *
 * Dev: human-readable console output at `debug` level by default.
 * Prod: structured JSON lines at `info` level by default (pipes into
 *        log aggregators like Datadog / Loki / Grafana without a
 *        parser). Non-string args are scrubbed for obvious PII
 *        keys before serialising.
 *
 * Level can be overridden at build time via `VITE_LOG_LEVEL`:
 *   - `silent` → nothing prints
 *   - `error`  → only error()
 *   - `warn`   → error + warn
 *   - `info`   → error + warn + info   (production default)
 *   - `debug`  → all four              (development default)
 *
 * Format can be forced via `VITE_LOG_FORMAT=json|text`. Default is
 * text in dev, json in prod.
 *
 * Rate-limiting: each (level, first-arg-message) key has a sliding
 * 60 s budget. Once exceeded, additional calls are dropped and a
 * single synthesized warn-line reports the drop count at window
 * close. Opt-out via `VITE_LOG_RATE_LIMIT=off` (tests, one-shot CLIs).
 */

const isDev = import.meta.env.DEV;

type LogLevel = 'silent' | 'error' | 'warn' | 'info' | 'debug';
type LogFormat = 'text' | 'json';

const LEVEL_RANK: Record<LogLevel, number> = {
  silent: 0,
  error: 1,
  warn: 2,
  info: 3,
  debug: 4,
};

function resolveLevel(): LogLevel {
  const raw = (import.meta.env.VITE_LOG_LEVEL as string | undefined)?.toLowerCase();
  if (raw && raw in LEVEL_RANK) return raw as LogLevel;
  return isDev ? 'debug' : 'info';
}

function resolveFormat(): LogFormat {
  const raw = (import.meta.env.VITE_LOG_FORMAT as string | undefined)?.toLowerCase();
  if (raw === 'json' || raw === 'text') return raw;
  return isDev ? 'text' : 'json';
}

// Mutable så window.__FLOW_DEBUG.setLevel() kan override runtime.
let activeLevel = LEVEL_RANK[resolveLevel()];
const activeFormat: LogFormat = resolveFormat();

function enabled(level: LogLevel): boolean {
  return LEVEL_RANK[level] <= activeLevel;
}

/** Keys whose values are stripped before serialisation. */
const PII_KEYS = new Set([
  'password', 'token', 'access_token', 'refresh_token', 'apikey', 'api_key',
  'authorization', 'cookie', 'session', 'secret', 'ssn', 'credit_card',
]);

// ── Regex pipeline for PII buried inside string *values* (not just
// keys). Conservative by design: we'd rather miss a match than
// false-positive on block-ids or hashes. Applied only to strings
// that pass a cheap pre-check, so the common case (plain log msg)
// stays fast.

// RFC-5322-lite: letters/digits/._%+- before @, domain with TLD.
const EMAIL_RE = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g;
// IPv4 with a strict 0-255 per-octet check so we don't redact
// version strings like `1.2.3.4.5` or `999.1.2.3`.
const IPV4_RE = /\b(?:(?:25[0-5]|2[0-4][0-9]|1?[0-9]?[0-9])\.){3}(?:25[0-5]|2[0-4][0-9]|1?[0-9]?[0-9])\b/g;
// IPv6: 8 groups of 1-4 hex; we keep it strict (no ::) to avoid
// matching on UUID fragments. The main goal is to catch the common
// fully-expanded form in server logs.
const IPV6_RE = /\b(?:[0-9A-Fa-f]{1,4}:){7}[0-9A-Fa-f]{1,4}\b/g;
// Bearer + JWT (3 base64url segments) in any position of a value.
const JWT_RE = /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g;
// Credit-card-looking 13–19-digit runs. Intentionally loose — the
// stricter Luhn check would require more code and miss test cards.
// `\d (sep? \d){12,18}` = 13–19 total digits, no trailing separator.
const CARD_RE = /\b\d(?:[ -]?\d){12,18}\b/g;

/**
 * Strip obvious PII patterns from a string value. Returns the string
 * unchanged when no patterns match (common path). The cheap `indexOf`
 * pre-check keeps the happy path allocation-free.
 */
function scrubString(s: string): string {
  let out = s;
  // Email
  if (out.indexOf('@') !== -1) out = out.replace(EMAIL_RE, '[email]');
  // IPv4 / IPv6
  if (/\d/.test(out)) out = out.replace(IPV4_RE, '[ip]');
  if (out.indexOf(':') !== -1) out = out.replace(IPV6_RE, '[ip]');
  // JWT
  if (out.indexOf('eyJ') !== -1) out = out.replace(JWT_RE, '[jwt]');
  // Credit-card-like runs, only if the string looks long enough.
  if (out.length >= 13 && /\d/.test(out)) out = out.replace(CARD_RE, '[card]');
  return out;
}

function scrub(value: unknown, depth = 0, seen: WeakSet<object> = new WeakSet()): unknown {
  if (depth > 4) return '[depth limit]';
  if (value == null) return value;
  if (typeof value === 'string') return scrubString(value);
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (value instanceof Error) {
    return { name: value.name, message: scrubString(value.message), stack: value.stack };
  }
  if (typeof value === 'object') {
    if (seen.has(value as object)) return '[circular]';
    seen.add(value as object);
    if (Array.isArray(value)) return value.map(v => scrub(v, depth + 1, seen));
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (PII_KEYS.has(k.toLowerCase())) {
        out[k] = '[redacted]';
      } else {
        out[k] = scrub(v, depth + 1, seen);
      }
    }
    return out;
  }
  return String(value);
}

/** Exported for tests. Deliberately internal — callers should use `logger.*`. */
export const _scrubString = scrubString;

interface LogEntry {
  ts: string;
  level: Exclude<LogLevel, 'silent'>;
  msg: string;
  context?: unknown;
}

function buildEntry(level: Exclude<LogLevel, 'silent'>, args: unknown[]): LogEntry {
  const msg = typeof args[0] === 'string' ? args[0] : 'log';
  const context = args.length > 1
    ? (args.length === 2 ? scrub(args[1]) : args.slice(1).map(a => scrub(a)))
    : typeof args[0] !== 'string' && args[0] != null
      ? scrub(args[0])
      : undefined;
  return {
    ts: new Date().toISOString(),
    level,
    msg,
    ...(context !== undefined ? { context } : {}),
  };
}

function rawEmit(level: Exclude<LogLevel, 'silent'>, args: unknown[]): void {
  if (!enabled(level)) return;
  const sink = level === 'error' ? console.error
    : level === 'warn' ? console.warn
    : level === 'info' ? console.info
    : console.debug;

  if (activeFormat === 'json') {
    sink(JSON.stringify(buildEntry(level, args)));
  } else {
    sink(...args);
  }
}

/**
 * Per-level rate budgets for a 60 s sliding window. Errors are rarer
 * and more valuable than debug spam, so errors get a tight budget and
 * debug gets the largest. Busting a budget drops the surplus; a single
 * synthesized warn-line at window close reports the drop count.
 */
const WINDOW_MS = 60_000;
const BUDGETS: Record<Exclude<LogLevel, 'silent'>, number> = {
  error: 10,
  warn: 20,
  info: 20,
  debug: 50,
};

interface WindowState {
  count: number;
  dropped: number;
  resetAt: number;
  flushTimer?: ReturnType<typeof setTimeout>;
}

const rateLimitDisabled = (() => {
  const raw = (import.meta.env.VITE_LOG_RATE_LIMIT as string | undefined)?.toLowerCase();
  return raw === 'off' || raw === 'false' || raw === '0';
})();

const windows = new Map<string, WindowState>();

function rateLimitKey(level: Exclude<LogLevel, 'silent'>, args: unknown[]): string {
  const head = typeof args[0] === 'string' ? args[0] : '<non-string>';
  return `${level}::${head}`;
}

function scheduleFlush(key: string, level: Exclude<LogLevel, 'silent'>, state: WindowState): void {
  if (state.flushTimer) return;
  state.flushTimer = setTimeout(() => {
    const dropped = state.dropped;
    windows.delete(key);
    if (dropped > 0) {
      const msg = key.slice(level.length + 2);
      rawEmit('warn', [`[rate-limit] ${dropped} ${level} log(s) dropped for "${msg}"`]);
    }
  }, WINDOW_MS);
  // Don't keep the process alive just for the flush — Node only.
  const timer = state.flushTimer as { unref?: () => void };
  if (typeof timer?.unref === 'function') timer.unref();
}

function emit(level: Exclude<LogLevel, 'silent'>, args: unknown[]): void {
  if (!enabled(level)) return;

  // Buffer entry även om rate-limit senare droppar console-print — vi
  // vill ha ALLA events i ring-buffern för debug. Rate-limit skyddar
  // bara konsolen, inte vår diagnostik.
  pushToBuffer(buildEntry(level, args));

  if (rateLimitDisabled) {
    rawEmit(level, args);
    return;
  }
  const key = rateLimitKey(level, args);
  const now = Date.now();
  let state = windows.get(key);
  if (!state || now >= state.resetAt) {
    state = { count: 0, dropped: 0, resetAt: now + WINDOW_MS };
    windows.set(key, state);
  }
  const budget = BUDGETS[level];
  if (state.count >= budget) {
    state.dropped += 1;
    scheduleFlush(key, level, state);
    return;
  }
  state.count += 1;
  scheduleFlush(key, level, state);
  rawEmit(level, args);
}

/** Test-only: clear rate-limit bookkeeping between cases. */
export function _resetLoggerRateLimit(): void {
  for (const state of windows.values()) {
    if (state.flushTimer) clearTimeout(state.flushTimer);
  }
  windows.clear();
}

// ============================================================
// Ring-buffer + window.__FLOW_DEBUG runtime-inspector
// ============================================================
//
// Buffer:en behåller de senaste RING_BUFFER_MAX entries så vi kan dumpa
// dem i konsolen om något går fel i prod. Ingen extra nätverkstrafik,
// ingen storage-påverkan. Exponeras via `window.__FLOW_DEBUG` bara när
// `isDev` eller user har satt `localStorage.flow-debug-enabled=1`.
//
// Användning i browser-console:
//   __FLOW_DEBUG.logs()                    — dumpa senaste 100
//   __FLOW_DEBUG.logs({level:'error'})     — bara errors
//   __FLOW_DEBUG.logs({grep:'moderation'}) — filter på message
//   __FLOW_DEBUG.logs({limit:500})         — fler entries
//   __FLOW_DEBUG.copy()                    — kopiera hela buffer som JSON
//   __FLOW_DEBUG.clear()                   — nolla buffer
//   __FLOW_DEBUG.setLevel('debug')         — verbose runtime
//   __FLOW_DEBUG.setLevel('silent')        — kill switch
//   __FLOW_DEBUG.stats()                   — count per level + drops
// ============================================================

const RING_BUFFER_MAX = 500;
const ringBuffer: LogEntry[] = [];
const levelCounts: Record<Exclude<LogLevel, 'silent'>, number> = {
  error: 0, warn: 0, info: 0, debug: 0,
};

function pushToBuffer(entry: LogEntry): void {
  ringBuffer.push(entry);
  if (ringBuffer.length > RING_BUFFER_MAX) ringBuffer.shift();
  levelCounts[entry.level]++;
}

interface LogsFilter {
  level?: Exclude<LogLevel, 'silent'>;
  grep?: string;
  limit?: number;
  since?: string; // ISO timestamp
}

function filterBuffer(filter?: LogsFilter): LogEntry[] {
  let out = ringBuffer;
  if (filter?.level) out = out.filter(e => e.level === filter.level);
  if (filter?.grep) {
    const needle = filter.grep.toLowerCase();
    out = out.filter(e =>
      e.msg.toLowerCase().includes(needle) ||
      JSON.stringify(e.context ?? '').toLowerCase().includes(needle),
    );
  }
  if (filter?.since) {
    out = out.filter(e => e.ts >= filter.since!);
  }
  const limit = filter?.limit ?? 100;
  return out.slice(-limit);
}

function debugEnabled(): boolean {
  if (isDev) return true;
  if (typeof localStorage === 'undefined') return false;
  try {
    return localStorage.getItem('flow-debug-enabled') === '1';
  } catch { return false; }
}

export const loggerDebug = {
  logs: (filter?: LogsFilter): LogEntry[] => {
    const result = filterBuffer(filter);
    // Side-effect: print to console so user kan se direkt i DevTools
    // utan att behöva JSON.stringify:a resultatet.
    console.table(result.map(e => ({
      ts: e.ts.slice(11, 23), // HH:MM:SS.SSS
      level: e.level,
      msg: e.msg,
      context: e.context ? JSON.stringify(e.context).slice(0, 80) : '',
    })));
    return result;
  },
  copy: async (filter?: LogsFilter): Promise<boolean> => {
    const json = JSON.stringify(filterBuffer(filter), null, 2);
    try {
      await navigator.clipboard.writeText(json);
      return true;
    } catch {
      console.log(json);
      return false;
    }
  },
  clear: (): void => {
    ringBuffer.length = 0;
    levelCounts.error = 0;
    levelCounts.warn = 0;
    levelCounts.info = 0;
    levelCounts.debug = 0;
  },
  setLevel: (level: LogLevel): void => {
    activeLevel = LEVEL_RANK[level];
    console.log(`[logger] level → ${level}`);
  },
  stats: () => ({
    bufferSize: ringBuffer.length,
    bufferMax: RING_BUFFER_MAX,
    byLevel: { ...levelCounts },
    rateLimitDropped: Array.from(windows.values()).reduce((n, s) => n + s.dropped, 0),
    activeLevel: (Object.keys(LEVEL_RANK) as LogLevel[]).find(k => LEVEL_RANK[k] === activeLevel),
  }),
};

if (typeof window !== 'undefined' && debugEnabled()) {
  (window as unknown as { __FLOW_DEBUG: typeof loggerDebug }).__FLOW_DEBUG = loggerDebug;
}

export const logger = {
  error: (...args: unknown[]) => emit('error', args),
  warn: (...args: unknown[]) => emit('warn', args),
  info: (...args: unknown[]) => emit('info', args),
  debug: (...args: unknown[]) => emit('debug', args),
};
